import { useEffect, useState, useRef } from "react";
import { Loader2 } from "lucide-react";

interface CellData {
  v: unknown;
  c: string | null;
}

interface SheetResponse {
  sheet_name: string;
  sheets: string[];
  rows: CellData[][];
  row_count: number;
  truncated: boolean;
}

interface ExcelSourceViewProps {
  dealId: string;
  docId: string;
  activeCell: string | null;
  sheetName: string | null;
}

function colLetter(idx: number): string {
  let result = "";
  let n = idx + 1;
  while (n > 0) {
    n -= 1;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

export default function ExcelSourceView({
  dealId,
  docId,
  activeCell,
  sheetName,
}: ExcelSourceViewProps) {
  const [data, setData] = useState<SheetResponse | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(sheetName);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const activeCellRef = useRef<HTMLTableCellElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    import("../../api/client").then(({ default: api }) => {
      const params: Record<string, string> = {};
      if (selectedSheet) params.sheet = selectedSheet;
      api.get(`/api/deals/${dealId}/documents/${docId}/sheet-data`, { params })
        .then((res) => {
          if (!cancelled) {
            setData(res.data);
            setLoading(false);
          }
        })
        .catch(() => { if (!cancelled) { setLoadError("Could not load spreadsheet data. Check your connection."); setLoading(false); } });
    });

    return () => { cancelled = true; };
  }, [dealId, docId, selectedSheet]);

  // Update selected sheet when sheetName prop changes
  useEffect(() => {
    if (sheetName && sheetName !== selectedSheet) setSelectedSheet(sheetName);
  }, [sheetName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll the active cell into view. Using offsetLeft/offsetTop is fragile
  // because offsetParent depends on positioned ancestors and can vary inside
  // tables. getBoundingClientRect gives viewport coords that we translate to
  // the scroll container via its own rect + current scroll position, which
  // works reliably for any column (A-Z+) or row, accounting for the sticky
  // row-number column and column-letter header.
  //
  // Look up the target cell by data-coord instead of the ref. React assigns
  // refs in commit order (unmount old ref, then mount new ref), and stacking
  // the old smooth-scroll with a new call can leave activeCellRef pointing
  // at the wrong cell for one frame. querySelector reads the fresh DOM so
  // left-scrolls (e.g. L5 → E5) land on the correct cell every time.
  useEffect(() => {
    if (!activeCell) return;
    const container = scrollRef.current;
    if (!container) return;
    const raf = requestAnimationFrame(() => {
      const selector = `td[data-coord="${activeCell}"]`;
      const cell = container.querySelector<HTMLTableCellElement>(selector)
        ?? activeCellRef.current;
      if (!cell) return;
      const cellRect = cell.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const stickyColWidth = 32;
      const stickyRowHeight = 24;
      const relLeft = cellRect.left - containerRect.left + container.scrollLeft;
      const relTop = cellRect.top - containerRect.top + container.scrollTop;
      const targetLeft =
        relLeft -
        stickyColWidth -
        (container.clientWidth - stickyColWidth - cellRect.width) / 2;
      const targetTop =
        relTop -
        stickyRowHeight -
        (container.clientHeight - stickyRowHeight - cellRect.height) / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [activeCell, data, selectedSheet]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-forest-600" />
      </div>
    );
  }

  if (loadError) {
    return <div className="flex items-center justify-center h-full text-bark-400 text-sm p-4">{loadError}</div>;
  }

  if (!data || data.rows.length === 0) {
    return <div className="flex items-center justify-center h-full text-bark-400 text-sm">No data available</div>;
  }

  const maxCols = Math.max(...data.rows.map((r) => r.length));

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Sheet tabs */}
      {data.sheets.length > 1 && (
        <div className="flex gap-0.5 px-2 py-1 border-b border-grove-200 bg-grove-50 overflow-x-auto scrollbar-hide shrink-0">
          {data.sheets.map((s) => (
            <button
              key={s}
              onClick={() => setSelectedSheet(s)}
              className={`px-2.5 py-1 text-[10px] font-medium rounded-t whitespace-nowrap transition-colors ${
                s === data.sheet_name
                  ? "bg-white text-forest-700 border border-grove-200 border-b-white -mb-px"
                  : "text-bark-400 hover:text-bark-600 hover:bg-grove-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div ref={scrollRef} className="flex-1 overflow-auto relative">
        <table className="border-collapse text-xs">
          {/* Column letter headers */}
          <thead className="sticky top-0 z-10">
            <tr className="bg-grove-100 border-b border-grove-200">
              <th className="sticky left-0 z-20 px-1.5 py-1 bg-grove-100 border-r border-grove-200 w-8" />
              {Array.from({ length: maxCols }, (_, ci) => (
                <th
                  key={ci}
                  className="px-2 py-1 text-center text-[10px] font-mono font-medium text-bark-400 border-r border-grove-100 bg-grove-100"
                >
                  {colLetter(ci)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, ri) => (
              <tr key={ri} className="border-b border-grove-100">
                <td className="sticky left-0 z-10 px-1.5 py-1 bg-grove-50 text-bark-400 text-[10px] font-mono text-right border-r border-grove-200 select-none">
                  {ri + 1}
                </td>
                {row.map((cell, ci) => {
                  const isActive = activeCell != null && cell.c === activeCell;
                  return (
                    <td
                      key={ci}
                      ref={isActive ? activeCellRef : undefined}
                      data-coord={cell.c ?? undefined}
                      className={`px-2 py-1 border-r border-grove-100 whitespace-nowrap max-w-[200px] truncate ${
                        isActive
                          ? "bg-honey-100 border-2 border-honey-400 font-medium text-bark-800 animate-pulse-once"
                          : cell.v != null
                          ? "text-bark-700"
                          : "text-bark-300"
                      }`}
                      title={cell.v != null ? String(cell.v) : ""}
                    >
                      {cell.v != null ? formatCellValue(cell.v) : ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Truncation indicator */}
      {data.truncated && (
        <div className="shrink-0 px-3 py-1.5 text-[10px] text-bark-400 bg-grove-50 border-t border-grove-200 text-center">
          Showing first {data.row_count} rows
        </div>
      )}
    </div>
  );
}

function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return "";
  if (typeof val === "number") {
    if (Number.isInteger(val) && Math.abs(val) < 100000) return String(val);
    return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  const s = String(val);
  if (s.match(/^\d{4}-\d{2}-\d{2}T/)) return s.slice(0, 10);
  return s;
}
